<template>
  <div class="tct-root" :class="{ 'tct-open': open }">
    <!-- Backdrop (only on small screens — desktop tablet leaves the canvas
         visible behind the tray for continued patient-facing display). -->
    <div v-if="open" class="tct-backdrop" @click="onClose"></div>

    <!-- FAB (collapsed state). Single tap to open. Always reachable bottom-right. -->
    <button
      v-if="!open"
      class="tct-fab"
      type="button"
      @click="onOpen"
      :title="`TC Controls — ${viewOptionLabel(activeView)} lens`"
      aria-label="Open TC Controls"
    >
      <i class="fa fa-sliders"></i>
    </button>

    <!-- Expanded tray. Dark theme intentionally — visually separates "TC
         control surface" from the patient-facing canvas behind it, so a TC
         glance vs. a patient glance reads differently. -->
    <aside v-if="open" class="tct-panel" role="dialog" aria-label="TC Controls">
      <header class="tct-panel-header">
        <div class="tct-panel-title">
          <i class="fa fa-sliders"></i>
          <span>TC Controls</span>
        </div>
        <button class="tct-panel-close" @click="onClose" aria-label="Close controls">
          <i class="fa fa-times"></i>
        </button>
      </header>

      <!-- Floating close FAB — only visible on small screens (iPad-portrait
           side-drawer mode) where the header X scrolls out of reach. Sibling
           of header + body so its absolute positioning stays in the panel's
           viewport regardless of body scroll. -->
      <button
        class="tct-panel-close-fab"
        type="button"
        @click="onClose"
        aria-label="Close controls"
      >
        <i class="fa fa-times"></i>
      </button>

      <div class="tct-panel-body">
        <!-- ── Lens ───────────────────────────────────────────── -->
        <section class="tct-section">
          <div class="tct-section-label">Lens</div>
          <div class="tct-lens-grid">
            <button
              v-for="v in viewOptions"
              :key="v.id"
              class="tct-lens-tab"
              :class="{ active: activeView === v.id }"
              @click="onSelectView(v.id)"
              type="button"
            >{{ v.label }}</button>
          </div>
        </section>

        <!-- ── Send & handoff ─────────────────────────────────── -->
        <section class="tct-section">
          <div class="tct-section-label">Send &amp; handoff</div>
          <button
            class="tct-action-btn"
            @click="$emit('send-plan')"
            type="button"
          >
            <i class="fa fa-paper-plane"></i>
            <span class="tct-action-text">
              <span class="tct-action-title">Send to patient</span>
              <span class="tct-action-sub">Text, email, or copy link</span>
            </span>
          </button>
          <button
            class="tct-action-btn"
            type="button"
            @click="onRequestSignature"
            title="Show the signature pad so the patient can sign on this device"
          >
            <i class="fa fa-pencil-square-o"></i>
            <span class="tct-action-text">
              <span class="tct-action-title">Request signature on this device</span>
              <span class="tct-action-sub">Patient signs here, in-office</span>
            </span>
          </button>
        </section>

        <!-- ── Internal note (team-only) ───────────────────────── -->
        <section class="tct-section">
          <div class="tct-section-label">
            Internal note
            <span class="tct-section-hint">Team-only · not shown to patient</span>
          </div>
          <textarea
            v-model="noteDraft"
            class="tct-note-input"
            rows="4"
            placeholder="Notes for your team — e.g., 'mentioned anxiety about extractions'"
            @blur="onBlurNote"
          ></textarea>
          <div class="tct-note-meta">
            <span v-if="noteSaving" class="tct-note-saving">
              <i class="fa fa-circle-o-notch fa-spin"></i> Saving…
            </span>
            <span v-else-if="noteDirty" class="tct-note-dirty">Unsaved changes</span>
            <span v-else-if="lastSavedAt" class="tct-note-saved">
              <i class="fa fa-check"></i> Saved
            </span>
          </div>
        </section>

        <!-- ── Financial controls (Slice 9e) ──────────────────── -->
        <section class="tct-section">
          <div class="tct-section-label">Financial</div>

          <!-- Monthly breakdown toggle + slider. Slider only shows when
               the toggle is on (saves vertical space when not in use). -->
          <label class="tct-toggle">
            <input type="checkbox" :checked="showMonthly" @change="$emit('update:show-monthly', $event.target.checked)" />
            <span class="tct-toggle-label">Show monthly breakdown</span>
          </label>
          <div v-if="showMonthly" class="tct-slider-row">
            <input
              type="range"
              min="6"
              max="60"
              step="1"
              :value="monthlyTerm"
              @input="$emit('update:monthly-term', parseInt($event.target.value, 10))"
              class="tct-slider"
              aria-label="Monthly payment term in months"
            />
            <span class="tct-slider-value">{{ monthlyTerm }} mo</span>
          </div>

          <label class="tct-toggle">
            <input type="checkbox" :checked="showPaymentOptions" @change="$emit('update:show-payment-options', $event.target.checked)" />
            <span class="tct-toggle-label">Show payment options</span>
          </label>
        </section>

        <!-- ── Pricing mode, per view (persisted — mirrors Builder) ────── -->
        <section class="tct-section">
          <div class="tct-section-label">Pricing shown</div>
          <div v-for="v in viewOptions" :key="'pm-' + v.id" class="tct-pricing-mode">
            <div class="tct-pricing-mode-view">{{ v.label }}</div>
            <div class="tct-segmented">
              <button
                v-for="m in pricingModeOptions"
                :key="v.id + '-' + m.id"
                type="button"
                class="tct-segmented-btn"
                :class="{ active: viewPricingModes[v.id] === m.id }"
                :title="m.hint"
                @click="$emit('set-pricing-mode', v.id, m.id)"
              >{{ m.label }}</button>
            </div>
          </div>
        </section>

        <!-- ── Fee breakdown lines (persisted — mirrors Builder Financials).
             Global, NOT per-view: these say which sub-lines appear wherever a
             view is set to Full pricing. ─────────────────────────────────── -->
        <section class="tct-section">
          <div class="tct-section-label">
            Fee breakdown lines
            <span class="tct-section-hint">Where pricing is Full</span>
          </div>
          <label class="tct-toggle">
            <input type="checkbox" :checked="!!pricingLayers.netFee" @change="$emit('toggle-pricing-layer', 'netFee')" />
            <span class="tct-toggle-label">Net Fee <span class="tct-toggle-sub">PPO Fee</span></span>
          </label>
          <label class="tct-toggle">
            <input type="checkbox" :checked="!!pricingLayers.standardFee" @change="$emit('toggle-pricing-layer', 'standardFee')" />
            <span class="tct-toggle-label">Standard fee <span class="tct-toggle-sub">UCR</span></span>
          </label>
          <label class="tct-toggle">
            <input type="checkbox" :checked="!!pricingLayers.insuranceEstimate" @change="$emit('toggle-pricing-layer', 'insuranceEstimate')" />
            <span class="tct-toggle-label">Insurance estimate</span>
          </label>
          <label class="tct-toggle">
            <input type="checkbox" :checked="!!pricingLayers.membership" @change="$emit('toggle-pricing-layer', 'membership')" />
            <span class="tct-toggle-label">Show Procedure Discounts <span class="tct-toggle-sub">OD Discount column</span></span>
          </label>
          <!-- Guarantor account balance / credit, folded into the portion. -->
          <label class="tct-toggle">
            <input type="checkbox" :checked="!!showBalance" @change="$emit('toggle-show-balance')" />
            <span class="tct-toggle-label">Show balance/credits from Open Dental</span>
          </label>
          <div class="tct-toggle-desc">This is the balance listed under the Guarantor of the Account in Open Dental</div>
          <!-- Per-group subtotals — only when Itemized is grouped by priority. -->
          <label class="tct-toggle" :class="{ 'tct-toggle-muted': !itemizedGroupByPriority }">
            <input
              type="checkbox"
              :disabled="!itemizedGroupByPriority"
              :checked="itemizedGroupSubtotals"
              @change="$emit('toggle-group-subtotals')"
            />
            <span class="tct-toggle-label">
              Per-group subtotals
              <span class="tct-toggle-sub">Itemized · grouped</span>
            </span>
          </label>
        </section>

        <!-- ── Photos (persisted — mirrors Builder) ───────────────────── -->
        <section class="tct-section">
          <div class="tct-section-label">Photos</div>
          <label class="tct-toggle">
            <input type="checkbox" :checked="!!showPhotos" @change="$emit('toggle-show-photos')" />
            <span class="tct-toggle-label">Show Photos in Tx Plan</span>
          </label>
          <div class="tct-toggle-desc" v-if="patientPhotoCount === 0">
            No photos attached yet — use Attach Photos in the top bar.
          </div>
          <div class="tct-toggle-desc" v-else>
            {{ planPhotoCount }} of {{ patientPhotoCount }}
            {{ patientPhotoCount === 1 ? 'photo' : 'photos' }} apply to this plan.
          </div>
        </section>

        <!-- ── Itemized layout (persisted — mirrors Builder) ──────────── -->
        <section class="tct-section">
          <div class="tct-section-label">Itemized layout</div>
          <label class="tct-toggle">
            <input type="checkbox" :checked="itemizedGroupByPriority" @change="$emit('toggle-itemized-grouping')" />
            <span class="tct-toggle-label">
              Group by priority
              <span class="tct-toggle-sub">Itemized view only — OD priority groups + subtotals</span>
            </span>
          </label>

          <!-- Group-header word: preset pills quick-fill the input; type any
               custom word directly. Only when grouping is on. -->
          <div v-if="itemizedGroupByPriority" class="tct-grouplabel">
            <div class="tct-grouplabel-caption">Label each group as</div>
            <div class="tct-grouplabel-opts">
              <button
                v-for="w in ['Priority', 'Visit', 'Phase']"
                :key="w"
                type="button"
                class="tct-grouplabel-pill"
                :class="{ active: itemizedGroupLabel === w }"
                @click="$emit('set-group-label', w)"
              >{{ w }}</button>
            </div>
            <input
              type="text"
              class="tct-grouplabel-input"
              maxlength="24"
              placeholder="Or type your own word"
              :value="itemizedGroupLabel"
              @change="$emit('set-group-label', $event.target.value)"
              @blur="$emit('set-group-label', $event.target.value)"
            />
          </div>
        </section>

        <!-- Slice 9c — privacy screen reminder. Lives outside this tray
             as a dedicated FAB so it's one tap from anywhere on the page;
             this hint just helps TCs discover the shortcut. -->
        <section class="tct-section tct-section-hint-only">
          <div class="tct-hint-line">
            <i class="fa fa-eye-slash"></i>
            <span>Privacy screen: the eye-icon button next to this tray (one-tap blackout)</span>
          </div>
        </section>
      </div>
    </aside>
  </div>
</template>

<script>
/**
 * Slice 9b — Floating TC Controls tray.
 *
 * Collapsible FAB at bottom-right of Present Mode. Tap to expand into a
 * dark-themed panel with chairside controls. Lens switcher, Send-to-
 * patient, internal note (team-only). Privacy screen / alt swap /
 * financial toggles / monthly slider land in 9c–9e.
 *
 * Stateless about plan data — receives view + note context as props,
 * emits events for the parent (TxPresenterPresentMode) to handle. The
 * parent owns the SendPlanModal mount and the internal-note save call.
 */
import { TX_PRICING_MODE_OPTIONS } from '../constants/views.js';

export default {
  name: 'TCControlsTray',
  props: {
    activeView: { type: String, default: 'region' },
    viewOptions: { type: Array, default: () => [] },
    internalNote: { type: String, default: '' },
    noteSaving: { type: Boolean, default: false },
    // Slice 9e — financial controls (Present Mode owns the values).
    showMonthly: { type: Boolean, default: false },
    monthlyTerm: { type: Number, default: 24 },
    showPaymentOptions: { type: Boolean, default: false },
    // Persisted pricing display (mirrors the Builder Financials tab). Parent
    // owns these (read from annotations.pricingDisplay) and persists on toggle.
    // Per-view pricing mode: 'full' | 'total' | 'none'. See utils/resolveDisplay.js.
    viewPricingModes: { type: Object, default: () => ({ region: 'full', itemized: 'full' }) },
    pricingLayers: {
      type: Object,
      default: () => ({ netFee: true, standardFee: true, insuranceEstimate: true, membership: true }),
    },
    // "Show balance/credits from Open Dental" — top-level pricingDisplay flag
    // (not part of pricingLayers). Parent resolves + persists it.
    showBalance: { type: Boolean, default: false },
    // "Show Photos in Tx Plan" — same top-level pricingDisplay flag shape as
    // showBalance. Counts are display-only: how many of the patient's photos
    // this plan actually shows, so the TC can tell an empty gallery from a
    // gallery they turned off.
    showPhotos: { type: Boolean, default: false },
    patientPhotoCount: { type: Number, default: 0 },
    planPhotoCount: { type: Number, default: 0 },
    itemizedGroupByPriority: { type: Boolean, default: false },
    itemizedGroupLabel: { type: String, default: 'Priority' },
    itemizedGroupSubtotals: { type: Boolean, default: true },
  },
  data() {
    return {
      open: false,
      // Local draft so the user can type without every keystroke firing
      // a save. We emit save-note on blur (parent persists via
      // saveInternalNote). Two-way binding through `update:internal-note`
      // event keeps the parent in sync for downstream uses.
      noteDraft: this.internalNote || '',
      lastSavedAt: null,
      // Shared with the Builder Financials tab and practice Settings, so the
      // TC meets identical wording wherever they change this.
      pricingModeOptions: TX_PRICING_MODE_OPTIONS,
    };
  },
  computed: {
    noteDirty() {
      return (this.noteDraft || '') !== (this.internalNote || '');
    },
  },
  watch: {
    internalNote(newVal) {
      // Hydrate the draft on initial load (or external updates — e.g., a
      // teammate edited the note from the Builder Notes tab in another
      // tab). Skip when the user is actively editing — `noteDirty` flag
      // protects unsaved local edits from being clobbered.
      if (!this.noteDirty || this.noteDraft === '') {
        this.noteDraft = newVal || '';
      }
    },
  },
  methods: {
    onOpen() { this.open = true; },
    onClose() { this.open = false; },
    onSelectView(id) {
      this.$emit('update:active-view', id);
      // Don't auto-close on lens select — TC may want to compare lenses
      // back-to-back without re-opening the tray each time.
    },
    onBlurNote() {
      this.$emit('update:internal-note', this.noteDraft);
      if (this.noteDirty) {
        this.$emit('save-note', this.noteDraft);
        this.lastSavedAt = Date.now();
      }
    },
    onRequestSignature() {
      // Reveal the signature pad in the canvas (parent handles it). Close the
      // tray so the patient sees the plan + sign panel, not the TC controls.
      this.$emit('request-signature');
      this.open = false;
    },
    viewOptionLabel(id) {
      const o = this.viewOptions.find((v) => v.id === id);
      return (o && o.label) || '';
    },
  },
};
</script>

<style scoped>
.tct-root {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 60;  /* above Present Mode canvas (z-index 50) */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  pointer-events: none;
}
.tct-root > * { pointer-events: auto; }
.tct-root * { box-sizing: border-box; }

/* ── FAB ────────────────────────────────────────────────────── */
.tct-fab {
  position: absolute;
  bottom: 22px;
  right: 22px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #0d5f54;
  color: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.22), 0 2px 6px rgba(15, 23, 42, 0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, background 0.15s;
}
.tct-fab:hover {
  background: #094840;
  transform: scale(1.04);
}
.tct-fab:active { transform: scale(0.98); }
.tct-fab .fa { font-size: 20px; }

/* ── Backdrop (mobile/portrait only) ───────────────────────── */
.tct-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: none;
}

/* ── Panel ─────────────────────────────────────────────────── */
.tct-panel {
  position: absolute;
  bottom: 22px;
  right: 22px;
  width: 340px;
  max-height: calc(100vh - 80px);
  background: #1e293b;          /* slate-800 */
  color: #f1f5f9;                /* slate-100 */
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: tct-panel-in 0.18s ease-out;
}
@keyframes tct-panel-in {
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.tct-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid #334155;  /* slate-700 */
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;
}
.tct-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #f1f5f9;
}
.tct-panel-title .fa { color: #5eead4; font-size: 13px; }
.tct-panel-close {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #334155;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tct-panel-close:hover { background: #334155; color: #fff; }
.tct-panel-close .fa { font-size: 11px; }

.tct-panel-body {
  padding: 12px 16px 16px;
  overflow-y: auto;
  flex: 1;
}

/* ── Section primitives ────────────────────────────────────── */
.tct-section {
  padding: 12px 0;
  border-bottom: 1px solid #334155;
}
.tct-section:last-child { border-bottom: none; }
.tct-section-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  color: #94a3b8;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.tct-section-hint {
  text-transform: none;
  letter-spacing: 0.2px;
  font-weight: 400;
  font-size: 10.5px;
  color: #64748b;
}

/* ── Per-view pricing mode ──
   Segmented rather than a dropdown so "Total only" is visible without being
   hunted for. Dark-panel palette to match the rest of the chairside tray. */
.tct-pricing-mode + .tct-pricing-mode { margin-top: 10px; }
.tct-pricing-mode-view {
  font-size: 12px;
  color: #e2e8f0;
  margin-bottom: 5px;
}
.tct-segmented {
  display: flex;
  border: 1px solid #334155;
  border-radius: 8px;
  overflow: hidden;
}
.tct-segmented-btn {
  flex: 1;
  padding: 7px 4px;
  border: none;
  border-right: 1px solid #334155;
  background: transparent;
  color: #94a3b8;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.3;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.tct-segmented-btn:last-child { border-right: none; }
.tct-segmented-btn:hover:not(.active) { background: #1e293b; }
.tct-segmented-btn.active {
  background: #0f766e;
  color: #ffffff;
  font-weight: 600;
}

/* ── Lens grid ─────────────────────────────────────────────── */
.tct-lens-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
}
.tct-lens-tab {
  padding: 8px 6px;
  border: 1px solid #334155;
  background: #0f172a;          /* slate-900 */
  color: #cbd5e1;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.tct-lens-tab:hover {
  background: #1e293b;
  border-color: #475569;
  color: #f1f5f9;
}
.tct-lens-tab.active {
  background: #134e4a;          /* teal-900-ish */
  border-color: #0d9488;        /* teal-600 */
  color: #99f6e4;               /* teal-200 */
}

/* ── Action button (Send / Sign) ───────────────────────────── */
.tct-action-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #334155;
  background: #0f172a;
  color: #f1f5f9;
  border-radius: 8px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  margin-bottom: 6px;
  transition: background 0.1s, border-color 0.1s;
}
.tct-action-btn:last-child { margin-bottom: 0; }
.tct-action-btn:hover { background: #1e293b; border-color: #475569; }
.tct-action-btn .fa {
  font-size: 14px;
  color: #5eead4;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}
.tct-action-text { display: flex; flex-direction: column; line-height: 1.3; }
.tct-action-title { font-size: 13px; font-weight: 500; }
.tct-action-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.tct-action-btn-muted {
  opacity: 0.65;
  cursor: not-allowed;
}
.tct-action-btn-muted:hover {
  background: #0f172a;
  border-color: #334155;
}
.tct-action-btn-muted .fa { color: #64748b; }

/* ── Internal note ─────────────────────────────────────────── */
.tct-note-input {
  width: 100%;
  padding: 8px 10px;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 8px;
  color: #f1f5f9;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  min-height: 60px;
}
.tct-note-input:focus {
  outline: none;
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
}
.tct-note-input::placeholder { color: #64748b; }
.tct-note-meta {
  margin-top: 6px;
  font-size: 11px;
  color: #94a3b8;
  min-height: 16px;
}
.tct-note-saving { color: #cbd5e1; }
.tct-note-dirty { color: #fbbf24; }
.tct-note-saved { color: #5eead4; }
.tct-note-saved .fa { font-size: 10px; }

/* Group-header word picker (dark tray theme) */
.tct-grouplabel { margin: 4px 0 2px 24px; }
.tct-grouplabel-caption {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  margin-bottom: 6px;
}
.tct-grouplabel-opts { display: flex; flex-wrap: wrap; gap: 6px; }
.tct-grouplabel-pill {
  border: 1px solid #334155;
  background: #0f172a;
  color: #cbd5e1;
  border-radius: 999px;
  padding: 4px 11px;
  font-size: 11.5px;
  font-weight: 500;
  cursor: pointer;
}
.tct-grouplabel-pill:hover { background: #1e293b; border-color: #475569; }
.tct-grouplabel-pill.active {
  background: #134e4a;
  border-color: #0d9488;
  color: #99f6e4;
}
.tct-grouplabel-input {
  margin-top: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 9px;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 6px;
  color: #f1f5f9;
  font-size: 12px;
}
.tct-grouplabel-input:focus {
  outline: none;
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
}

/* ── Coming-next placeholders ──────────────────────────────── */
.tct-section-coming { opacity: 0.55; }
.tct-coming-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: #cbd5e1;
}
.tct-coming-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}
.tct-coming-list .fa {
  width: 14px;
  color: #94a3b8;
  font-size: 11px;
  text-align: center;
}

/* ── Slice 9e — Financial controls ───────────────────────────
   Toggle rows + range slider for monthly term. All controls share the
   slate panel theme — checkboxes in teal-accent. */
.tct-toggle {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  cursor: pointer;
  margin: 0;
}
.tct-toggle:hover .tct-toggle-label { color: #f1f5f9; }
.tct-toggle input[type='checkbox'] {
  margin: 3px 0 0;
  width: 14px;
  height: 14px;
  accent-color: #0d9488;
  cursor: pointer;
  flex-shrink: 0;
}
.tct-toggle-label {
  font-size: 12.5px;
  font-weight: 500;
  color: #cbd5e1;
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.tct-toggle-sub {
  font-size: 10.5px;
  color: #64748b;
  font-weight: 400;
  margin-top: 1px;
}
/* Full-sentence helper under a toggle (aligned past the 14px box + 10px gap). */
.tct-toggle-desc {
  margin: -4px 0 4px 24px;
  font-size: 10.5px;
  line-height: 1.35;
  color: #64748b;
}
.tct-toggle-muted {
  opacity: 0.55;
  cursor: not-allowed;
}
.tct-toggle-muted:hover .tct-toggle-label { color: #cbd5e1; }
.tct-toggle-muted input[type='checkbox'] { cursor: not-allowed; }

.tct-slider-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 2px 0 4px 24px;  /* indent under the toggle */
}
.tct-slider {
  flex: 1;
  height: 4px;
  background: #334155;
  border-radius: 4px;
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  cursor: pointer;
}
.tct-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #5eead4;
  border: 2px solid #0f172a;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
.tct-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #5eead4;
  border: 2px solid #0f172a;
  cursor: pointer;
}
.tct-slider-value {
  font-size: 12px;
  font-weight: 600;
  color: #99f6e4;
  font-variant-numeric: tabular-nums;
  min-width: 44px;
  text-align: right;
}

/* Slice 9c — privacy-screen discovery hint */
.tct-section-hint-only { padding-top: 10px; padding-bottom: 4px; }
.tct-hint-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.5;
}
.tct-hint-line .fa {
  color: #5eead4;
  font-size: 11px;
  margin-top: 2px;
}

/* ── Tablet portrait / small viewport ──────────────────────── */
/* iPad portrait (≈768px) and narrower — a fixed 340px panel crowds the canvas
   on a tablet, so stretch it to a full-width bottom sheet (with the dimming
   backdrop) and give the close button a finger-sized tap target. */
@media (max-width: 820px) {
  .tct-backdrop { display: block; }
  /* iPad-portrait + similar narrow widths — slide the panel in from the right
     as a ~380px side drawer so the canvas behind it stays partially visible
     and the TC sees live changes as they toggle settings. (Was a full-width
     bottom sheet, which obscured the plan entirely.) */
  .tct-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    width: 380px;
    max-width: 85vw;
    max-height: 100vh;
    border-radius: 14px 0 0 14px;
    animation: tct-panel-slide-in 0.18s ease-out;
  }
  .tct-panel-close { width: 40px; height: 40px; }
  /* Floating close FAB shows only at this breakpoint (always reachable while
     the user scrolls the panel body). */
  .tct-panel-close-fab { display: flex; }
}
@keyframes tct-panel-slide-in {
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
}

/* The close FAB — sticky bottom-right inside the panel, thumb-friendly.
   Hidden by default; the @media (max-width: 820px) block above flips it on. */
.tct-panel-close-fab {
  display: none;
  position: absolute;
  bottom: 22px;
  right: 22px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: #0d5f54;
  color: #fff;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.32), 0 2px 6px rgba(15, 23, 42, 0.14);
  z-index: 5;
  transition: background 0.12s, transform 0.12s;
}
.tct-panel-close-fab:hover { background: #094840; }
.tct-panel-close-fab:active { transform: scale(0.96); }
</style>
